import { supabase } from './supabase';
import {
  RELEASE_BODY,
  RELEASE_TITLE,
  cleanReleaseVersion,
} from '../../supabase/functions/_shared/appVersion.ts';

export { RELEASE_BODY, RELEASE_TITLE, cleanReleaseVersion };

export interface ReleaseSettings {
  id: string;
  announce_releases: boolean;
  app_store_url: string | null;
  play_store_url: string;
}

export interface Announcement {
  id: string;
  version: string;
  outcome: 'sending' | 'sent' | 'partial' | 'failed' | 'empty';
  ios_sent: number;
  ios_failed: number;
  android_sent: number;
  android_failed: number;
  first_error: string | null;
  created_at: string;
}

export interface Reach {
  /** The version this count is for (the one typed, or the newest a phone reports). */
  version: string | null;
  /** The newest version any phone reports, and how many phones are on it. */
  latest: { version: string; devices: number } | null;
  ios: number;
  android: number;
  already: boolean;
}

/** The same rules as the database check, so a wrong link is caught before saving. */
export const APP_STORE_URL = /^https:\/\/apps\.apple\.com\/\S+$/;
export const PLAY_STORE_URL = /^https:\/\/play\.google\.com\/store\/apps\/details\?id=[A-Za-z0-9._]+(&\S*)?$/;

export async function loadReleaseSettings(): Promise<ReleaseSettings | null> {
  const { data } = await supabase
    .from('platform_settings')
    .select('id, announce_releases, app_store_url, play_store_url')
    .limit(1)
    .single();
  return (data as ReleaseSettings | null) ?? null;
}

export async function saveReleaseSettings(id: string, patch: Partial<Omit<ReleaseSettings, 'id'>>): Promise<void> {
  const { error } = await supabase
    .from('platform_settings')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function loadAnnouncements(): Promise<Announcement[]> {
  const { data } = await supabase
    .from('release_announcements')
    .select('id, version, outcome, ios_sent, ios_failed, android_sent, android_failed, first_error, created_at')
    .order('created_at', { ascending: false })
    .limit(10);
  return (data ?? []) as Announcement[];
}

async function call<T>(version: string | undefined, dryRun: boolean): Promise<T> {
  const { data, error } = await supabase.functions.invoke('announce-release', { body: { version, dry_run: dryRun } });
  if (error) {
    let body: { error?: string; already?: boolean } | null = null;
    try { body = await (error as { context?: Response }).context?.json?.(); } catch { /* use the default message */ }
    if (dryRun && body?.already) return { version: version ?? null, latest: null, ios: 0, android: 0, already: true } as T;
    throw new Error(body?.error || 'Something went wrong. Please try again.');
  }
  return data as T;
}

/**
 * Counts who an announcement of this version would reach. Sends nothing. With no version it
 * uses the newest one any phone reports (and says which, in `version` and `latest`).
 */
export async function previewAnnouncement(version?: string): Promise<Reach> {
  const r = await call<Partial<Reach>>(version, true);
  return { version: r.version ?? null, latest: r.latest ?? null, ios: r.ios ?? 0, android: r.android ?? 0, already: !!r.already };
}

export interface AnnounceResult {
  version: string;
  outcome: Announcement['outcome'];
  sent: number;
  failed: number;
  errors?: string[];
}

export function announceRelease(version: string): Promise<AnnounceResult> {
  return call<AnnounceResult>(version, false);
}
